import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import Header from '../components/Header';
import Navigation from '../components/Navigation';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { FileText, ExternalLink, Download, Eye, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PolicyPage = ({ user, setUser }) => {
  const [policies, setPolicies] = useState([]);
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [iframeError, setIframeError] = useState(false);
  const [iframeLoading, setIframeLoading] = useState(false);

  // Check if user can download policies (Admin, SuperAdmin, or HR department)
  const canDownload = user?.role === 'admin' || 
                      user?.role === 'superadmin' || 
                      user?.department?.toLowerCase() === 'hr' || 
                      user?.department?.toLowerCase() === 'human resources';

  const fetchPolicies = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${API}/policies`, config);
      setPolicies(response.data || []);
      if (response.data && response.data.length > 0) {
        setSelectedPolicy(response.data[0]);
      }
    } catch (error) {
      console.error('Failed to load policies:', error);
      toast.error('Failed to load policies');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Add global error handler to suppress cross-origin iframe errors
    const originalOnError = window.onerror;
    window.onerror = function(message, source, lineno, colno, error) {
      // Suppress "Script error." which is a cross-origin error from iframes
      if (message === 'Script error.' || message === 'Script error') {
        return true; // Prevents the error from being logged
      }
      // Call original handler for other errors
      if (originalOnError) {
        return originalOnError(message, source, lineno, colno, error);
      }
      return false;
    };

    fetchPolicies();

    // Cleanup on unmount
    return () => {
      window.onerror = originalOnError;
    };
  }, [fetchPolicies]);

  // Old inline fetchPolicies removed; replaced by useCallback above.

  const handlePolicyChange = (policyId) => {
    if (!policyId) return;
    const policy = policies.find(p => p.id === policyId);
    if (policy) {
      setSelectedPolicy(policy);
      setIframeError(false);
      setIframeLoading(true);
    }
  };

  // Generate different URL formats for Google Drive
  const getPreviewUrl = (fileId) => {
    // Use Google Docs viewer for better compatibility
    return `https://drive.google.com/file/d/${fileId}/preview`;
  };

  const getViewUrl = (fileId) => {
    return `https://drive.google.com/file/d/${fileId}/view`;
  };

  const getDownloadUrl = (fileId) => {
    return `https://drive.google.com/uc?id=${fileId}&export=download`;
  };

  const getEmbedUrl = (fileId) => {
    // Alternative: Use Google Docs viewer which works better for PDFs
    return `https://docs.google.com/viewer?srcid=${fileId}&pid=explorer&efh=false&a=v&chrome=false&embedded=true`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50" data-testid="policy-page">
      <Header user={user} setUser={setUser} />
      <Navigation user={user} />
      
      <div className="px-4 sm:px-6 lg:px-8 py-6 max-w-screen-2xl mx-auto w-full">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Company Policies</h1>
          <p className="text-gray-600">View and read company policy documents</p>
        </div>

        {loading ? (
          <div className="text-center py-12">Loading policies...</div>
        ) : policies.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-gray-500">
              No policies available
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* Policy Selector */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Select Policy</CardTitle>
              </CardHeader>
              <CardContent>
                <Select 
                  value={selectedPolicy?.id || ''} 
                  onValueChange={handlePolicyChange}
                >
                  <SelectTrigger className="w-full" data-testid="policy-select">
                    <SelectValue placeholder="Choose a policy" />
                  </SelectTrigger>
                  <SelectContent>
                    {policies.map(policy => (
                      <SelectItem key={policy.id} value={policy.id}>
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4" />
                          {policy.title}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </CardContent>
            </Card>

            {/* Policy Viewer */}
            {selectedPolicy && (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="w-5 h-5 text-blue-600" />
                      {selectedPolicy.title}
                    </CardTitle>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(getViewUrl(selectedPolicy.drive_file_id), '_blank')}
                        data-testid="view-in-drive-btn"
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        View in Drive
                      </Button>
                      {canDownload && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => window.open(getDownloadUrl(selectedPolicy.drive_file_id), '_blank')}
                          data-testid="download-policy-btn"
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Download
                        </Button>
                      )}
                      <a
                        href={getViewUrl(selectedPolicy.drive_file_id)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-700 flex items-center gap-1 text-sm"
                        data-testid="open-in-drive-link"
                      >
                        Open in Drive <ExternalLink className="w-4 h-4" />
                      </a>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* PDF Viewer */}
                  <div className="bg-gray-100 rounded-lg overflow-hidden relative" style={{ height: '700px' }}>
                    {iframeLoading && (
                      <div className="absolute inset-0 flex items-center justify-center bg-gray-100 z-10">
                        <div className="text-center">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                          <p className="text-gray-600">Loading document...</p>
                        </div>
                      </div>
                    )}
                    {!iframeError ? (
                      <iframe
                        key={selectedPolicy.id}
                        src={getPreviewUrl(selectedPolicy.drive_file_id)}
                        width="100%"
                        height="100%"
                        allow="autoplay"
                        className="border-0"
                        title={selectedPolicy.title}
                        data-testid="policy-iframe"
                        sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
                        onLoad={() => {
                          setIframeError(false);
                          setIframeLoading(false);
                        }}
                        onError={() => {
                          setIframeError(true);
                          setIframeLoading(false);
                        }}
                      />
                    ) : (
                      <div className="flex flex-col items-center justify-center h-full text-gray-500">
                        <AlertCircle className="w-12 h-12 mb-4" />
                        <p className="text-lg font-medium">Unable to preview document</p>
                        <p className="text-sm mt-2">Please use the buttons above to view or download the document.</p>
                      </div>
                    )}
                  </div>
                  
                  {/* Help text */}
                  <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-sm text-blue-800 flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span>
                        <strong>Note:</strong> If the document doesn't load in the viewer above, please click "View in Drive" or "Download" to access the document directly. 
                        The document must be shared publicly or with your organization for the preview to work.
                      </span>
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PolicyPage;
